import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import client from "@/lib/api";
import {
  ArrowRight,
  Package,
  Award,
  Sparkles,
  Globe2,
  Target,
  BarChart3,
} from "lucide-react";
import {
  VISIBLE_CATEGORY_ORDER,
  groupVisibleSections,
  CategoryTree,
} from "@/components/WikiCategoryTree";

export default function WikiList() {
  const { t, i18n } = useTranslation();
  const [pages, setPages] = useState([]);
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category");

  useEffect(() => {
    client.get("/wiki/pages", { params: { language: i18n.language } })
      .then(r => setPages(r.data))
      .catch(() => {});
  }, [i18n.language]);

  const visibleSections = useMemo(() => groupVisibleSections(pages), [pages]);

  const sectionsToRender = activeCategory
    ? visibleSections.filter(([cat]) => cat === activeCategory)
    : visibleSections;

  const totalEntries = visibleSections.reduce((acc, [, items]) => acc + items.length, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="wiki-list-page">
      <div className="mb-7 md:mb-10">
        <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] md:tracking-[0.3em] text-[var(--t-blue-glow)] uppercase">{t("wiki.indexEyebrow")}</span>
        <h1 className="font-display text-3xl md:text-5xl mt-2 text-[var(--t-text)] uppercase leading-tight">{t("wiki.allEntries")}</h1>
      </div>

      {/* Top tile section — 2-col compact on mobile, 3-col with descriptions on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-8 md:mb-10">
        <Link to="/wiki/items" className="panel panel-glow corner-frame p-3 md:p-6 flex flex-col md:flex-row items-center md:items-center md:gap-4 gap-2 text-center md:text-left hover:border-[var(--t-blue)] transition group" data-testid="wiki-items-link">
          <div className="w-10 h-10 md:w-14 md:h-14 flex-shrink-0 inline-flex items-center justify-center border border-[var(--t-blue)] bg-[rgba(58,160,255,0.08)]">
            <Package className="w-5 h-5 md:w-7 md:h-7 text-[var(--t-blue-glow)] group-hover:text-[var(--t-cyan)] transition" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="hidden md:block font-mono text-[10px] tracking-[0.25em] text-[var(--t-blue-glow)] uppercase">// Sub-Section</div>
            <h2 className="font-display text-sm md:text-2xl text-[var(--t-cyan)] uppercase md:mt-0.5 group-hover:text-[var(--t-blue-glow)] transition">{t("wiki.items")}</h2>
            <p className="hidden md:block text-xs md:text-sm text-[var(--t-text-dim)] mt-0.5">{t("wiki.itemsDesc")}</p>
          </div>
          <ArrowRight className="hidden md:block w-5 h-5 text-[var(--t-blue-glow)] flex-shrink-0" />
        </Link>

        <Link to="/wiki/badges" className="panel panel-glow corner-frame p-3 md:p-6 flex flex-col md:flex-row items-center md:gap-4 gap-2 text-center md:text-left hover:border-[var(--t-gold)] transition group" data-testid="wiki-badges-link">
          <div className="w-10 h-10 md:w-14 md:h-14 flex-shrink-0 inline-flex items-center justify-center border border-[var(--t-gold)] bg-[rgba(243,197,82,0.08)]">
            <Award className="w-5 h-5 md:w-7 md:h-7 text-[var(--t-gold)] group-hover:text-[#ffd870] transition" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="hidden md:block font-mono text-[10px] tracking-[0.25em] text-[var(--t-gold)] uppercase">// Sub-Section</div>
            <h2 className="font-display text-sm md:text-2xl text-[var(--t-cyan)] uppercase md:mt-0.5 group-hover:text-[var(--t-gold)] transition">{t("wiki.badges")}</h2>
            <p className="hidden md:block text-xs md:text-sm text-[var(--t-text-dim)] mt-0.5">{t("wiki.badgesDesc")}</p>
          </div>
          <ArrowRight className="hidden md:block w-5 h-5 text-[var(--t-gold)] flex-shrink-0" />
        </Link>

        <Link to="/wiki/skills" className="panel panel-glow corner-frame p-3 md:p-6 flex flex-col md:flex-row items-center md:gap-4 gap-2 text-center md:text-left hover:border-[#e35a5a] transition group" data-testid="wiki-skills-link">
          <div className="w-10 h-10 md:w-14 md:h-14 flex-shrink-0 inline-flex items-center justify-center border border-[#e35a5a] bg-[rgba(227,90,90,0.08)]">
            <Sparkles className="w-5 h-5 md:w-7 md:h-7 text-[#e35a5a] group-hover:text-[#ff7878] transition" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="hidden md:block font-mono text-[10px] tracking-[0.25em] text-[#e35a5a] uppercase">// Sub-Section</div>
            <h2 className="font-display text-sm md:text-2xl text-[var(--t-cyan)] uppercase md:mt-0.5 group-hover:text-[#ff7878] transition">{t("wiki.skills")}</h2>
            <p className="hidden md:block text-xs md:text-sm text-[var(--t-text-dim)] mt-0.5">{t("wiki.skillsDesc")}</p>
          </div>
          <ArrowRight className="hidden md:block w-5 h-5 text-[#e35a5a] flex-shrink-0" />
        </Link>

        <Link to="/wiki/world" className="panel panel-glow corner-frame p-3 md:p-6 flex flex-col md:flex-row items-center md:gap-4 gap-2 text-center md:text-left hover:border-[#39FF14] transition group" data-testid="wiki-world-link">
          <div className="w-10 h-10 md:w-14 md:h-14 flex-shrink-0 inline-flex items-center justify-center border border-[#39FF14] bg-[rgba(57,255,20,0.08)]">
            <Globe2 className="w-5 h-5 md:w-7 md:h-7 text-[#39FF14] group-hover:text-[#8aff70] transition" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="hidden md:block font-mono text-[10px] tracking-[0.25em] text-[#39FF14] uppercase">// Tactical Map</div>
            <h2 className="font-display text-sm md:text-2xl text-[var(--t-cyan)] uppercase md:mt-0.5 group-hover:text-[#8aff70] transition">{t("wiki.world")}</h2>
            <p className="hidden md:block text-xs md:text-sm text-[var(--t-text-dim)] mt-0.5">{t("wiki.worldDesc")}</p>
          </div>
          <ArrowRight className="hidden md:block w-5 h-5 text-[#39FF14] flex-shrink-0" />
        </Link>

        <Link to="/wiki/missions" className="panel panel-glow corner-frame p-3 md:p-6 flex flex-col md:flex-row items-center md:gap-4 gap-2 text-center md:text-left hover:border-[#d978ff] transition group" data-testid="wiki-missions-link">
          <div className="w-10 h-10 md:w-14 md:h-14 flex-shrink-0 inline-flex items-center justify-center border border-[#d978ff] bg-[rgba(217,120,255,0.08)]">
            <Target className="w-5 h-5 md:w-7 md:h-7 text-[#d978ff] group-hover:text-[#ecb0ff] transition" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="hidden md:block font-mono text-[10px] tracking-[0.25em] text-[#d978ff] uppercase">// Daily Operations</div>
            <h2 className="font-display text-sm md:text-2xl text-[var(--t-cyan)] uppercase md:mt-0.5 group-hover:text-[#ecb0ff] transition">{t("wiki.missions")}</h2>
            <p className="hidden md:block text-xs md:text-sm text-[var(--t-text-dim)] mt-0.5">{t("wiki.missionsDesc")}</p>
          </div>
          <ArrowRight className="hidden md:block w-5 h-5 text-[#d978ff] flex-shrink-0" />
        </Link>

        <Link to="/wiki/stats" className="panel panel-glow corner-frame p-3 md:p-6 flex flex-col md:flex-row items-center md:gap-4 gap-2 text-center md:text-left hover:border-[#22d3ee] transition group" data-testid="wiki-stats-link">
          <div className="w-10 h-10 md:w-14 md:h-14 flex-shrink-0 inline-flex items-center justify-center border border-[#22d3ee] bg-[rgba(34,211,238,0.08)]">
            <BarChart3 className="w-5 h-5 md:w-7 md:h-7 text-[#22d3ee] group-hover:text-[#67e8f9] transition" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="hidden md:block font-mono text-[10px] tracking-[0.25em] text-[#22d3ee] uppercase">// Live Analytics</div>
            <h2 className="font-display text-sm md:text-2xl text-[var(--t-cyan)] uppercase md:mt-0.5 group-hover:text-[#67e8f9] transition">{t("wiki.stats")}</h2>
            <p className="hidden md:block text-xs md:text-sm text-[var(--t-text-dim)] mt-0.5">{t("wiki.statsDesc")}</p>
          </div>
          <ArrowRight className="hidden md:block w-5 h-5 text-[#22d3ee] flex-shrink-0" />
        </Link>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6 md:mb-8" data-testid="category-filter">
        <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-[var(--t-text-muted)] uppercase mr-2">{t("wiki.filter")}</span>
        <button
          onClick={() => setParams({})}
          className={`px-3 py-1.5 text-[10px] md:text-xs font-mono tracking-[0.18em] uppercase border transition ${
            !activeCategory
              ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
              : "border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:border-[var(--t-border-hi)]"
          }`}
          data-testid="filter-all"
        >
          {t("wiki.all")} <span className="text-[9px] ml-1 opacity-70">[{totalEntries}]</span>
        </button>
        {visibleSections.map(([cat, items]) => (
          <button
            key={cat}
            onClick={() => setParams({ category: cat })}
            className={`px-3 py-1.5 text-[10px] md:text-xs font-mono tracking-[0.18em] uppercase border transition ${
              activeCategory === cat
                ? "border-[var(--t-blue)] text-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)]"
                : "border-[var(--t-border)] text-[var(--t-text-dim)] hover:text-[var(--t-text)] hover:border-[var(--t-border-hi)]"
            }`}
            data-testid={`filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {cat} <span className="text-[9px] ml-1 opacity-70">[{items.length}]</span>
          </button>
        ))}
      </div>

      {/* Section tree */}
      <div className="space-y-8 md:space-y-10">
        {sectionsToRender.map(([cat, items]) => (
          <CategoryTree key={cat} category={cat} entries={items} />
        ))}
      </div>
    </div>
  );
}
